import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sparkles,
  FileText,
  Loader2,
  CheckCircle,
  AlertCircle,
  Edit3,
  Save,
  Upload,
  Target,
  TrendingUp,
  AlertTriangle,
  Download
} from "lucide-react";
import ResumeUploadModal from "./ResumeUploadModal";

export const ResumeTailorModal = ({ job, application, onClose, onTailoringComplete }) => {
  const { token } = useAuth();
  const [resumes, setResumes] = useState([]);
  const [selectedResumeId, setSelectedResumeId] = useState("");
  const [tone, setTone] = useState("professional");
  const [loading, setLoading] = useState(false);
  const [loadingResumes, setLoadingResumes] = useState(true);
  const [tailoredResult, setTailoredResult] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    fetchResumes();
  }, []);

  const fetchResumes = async () => {
    try {
      const response = await axios.get(`${API}/resumes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResumes(response.data);
      if (response.data.length > 0) {
        setSelectedResumeId(response.data[0].id);
      }
    } catch (error) {
      console.error("Failed to fetch resumes:", error);
    } finally {
      setLoadingResumes(false);
    }
  };

  const handleTailor = async () => {
    if (!selectedResumeId) {
      toast.error("Please select a base resume");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/resumes/tailor`,
        {
          base_resume_id: selectedResumeId,
          job_id: job.id,
          application_id: application.id,
          tone: tone
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTailoredResult(response.data);
      setEditedContent(response.data.tailored_content);
      toast.success("Resume tailored successfully!");
      
      if (onTailoringComplete) {
        onTailoringComplete(response.data);
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to tailor resume";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!tailoredResult) return;

    setSavingEdit(true);
    try {
      await axios.put(
        `${API}/tailored-resumes/${tailoredResult.id}`,
        { tailored_content: editedContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTailoredResult({ ...tailoredResult, tailored_content: editedContent });
      setIsEditing(false);
      toast.success("Changes saved!");
    } catch (error) {
      toast.error("Failed to save changes");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleResumeUploaded = (newResume) => {
    setResumes([newResume, ...resumes]);
    setSelectedResumeId(newResume.id);
    setShowUploadModal(false);
  };

  const handleDownloadPdf = async () => {
    if (!tailoredResult?.id) {
      toast.error("No tailored resume to download");
      return;
    }
    
    try {
      const response = await axios.get(
        `${API}/tailored-resumes/${tailoredResult.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `resume-${job?.company || 'job'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Resume downloaded!");
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Failed to download resume");
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-yellow-600";
    return "text-red-500";
  };

  const getScoreBg = (score) => {
    if (score >= 80) return "bg-green-100";
    if (score >= 60) return "bg-yellow-100";
    return "bg-red-100";
  };

  return (
    <>
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="resume-tailor-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#2D3748]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Sparkles className="h-5 w-5 text-[#A3C3D8]" />
              Generate Tailored Resume
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4">
            {/* Job Info */}
            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              <h3 className="font-medium text-[#2D3748] mb-1">{job?.title}</h3>
              <p className="text-sm text-[#94A3B8]">{job?.company}</p>
            </div>

            {!tailoredResult ? (
              /* Configuration Section */
              <div className="space-y-6">
                {/* Resume Selection */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-[#555F67] flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Select Base Resume
                    </Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowUploadModal(true)}
                      className="text-[#4A728D] border-[#A3C3D8]"
                      data-testid="upload-new-resume-btn"
                    >
                      <Upload className="h-4 w-4 mr-1" />
                      Upload New
                    </Button>
                  </div>
                  
                  {loadingResumes ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-[#A3C3D8]" />
                    </div>
                  ) : resumes.length === 0 ? (
                    <div className="text-center py-8 bg-slate-50 rounded-lg">
                      <FileText className="h-10 w-10 mx-auto text-[#94A3B8]/50 mb-3" />
                      <p className="text-sm text-[#94A3B8] mb-3">No resumes uploaded yet</p>
                      <Button
                        onClick={() => setShowUploadModal(true)}
                        className="bg-[#A3C3D8] hover:bg-[#8FB3C9] text-[#152A38]"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Your Resume
                      </Button>
                    </div>
                  ) : (
                    <Select value={selectedResumeId} onValueChange={setSelectedResumeId}>
                      <SelectTrigger data-testid="resume-select">
                        <SelectValue placeholder="Select a resume" />
                      </SelectTrigger>
                      <SelectContent>
                        {resumes.map(resume => (
                          <SelectItem key={resume.id} value={resume.id}>
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-[#94A3B8]" />
                              {resume.name}
                              <span className="text-xs text-[#94A3B8]">
                                ({resume.file_type.toUpperCase()})
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* Tone Selection */}
                <div className="space-y-2">
                  <Label className="text-[#555F67]">Writing Tone</Label>
                  <Select value={tone} onValueChange={setTone}>
                    <SelectTrigger data-testid="tone-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="professional">Professional & Formal</SelectItem>
                      <SelectItem value="confident">Confident & Assertive</SelectItem>
                      <SelectItem value="concise">Concise & Direct</SelectItem>
                      <SelectItem value="creative">Creative & Dynamic</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Generate Button */}
                <Button
                  onClick={handleTailor}
                  disabled={!selectedResumeId || loading}
                  className="w-full bg-[#A3C3D8] hover:bg-[#8FB3C9] text-[#152A38] h-12"
                  data-testid="generate-tailored-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      Analyzing & Tailoring...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-5 w-5 mr-2" />
                      Generate Tailored Resume
                    </>
                  )}
                </Button>
              </div>
            ) : (
              /* Results Section */
              <div className="space-y-6">
                {/* Match Score & Stats */}
                <div className="grid grid-cols-3 gap-4">
                  {/* Match Score */}
                  <div className={`${getScoreBg(tailoredResult.match_score)} rounded-lg p-4 text-center`}>
                    <Target className={`h-6 w-6 mx-auto mb-2 ${getScoreColor(tailoredResult.match_score)}`} />
                    <p className={`text-3xl font-bold ${getScoreColor(tailoredResult.match_score)}`}>
                      {tailoredResult.match_score}%
                    </p>
                    <p className="text-sm text-[#555F67]">Match Score</p>
                  </div>

                  {/* Key Matches */}
                  <div className="bg-green-50 rounded-lg p-4">
                    <CheckCircle className="h-6 w-6 mx-auto mb-2 text-green-600" />
                    <p className="text-2xl font-bold text-green-600 text-center">
                      {tailoredResult.key_matches?.length || 0}
                    </p>
                    <p className="text-sm text-[#555F67] text-center">Key Matches</p>
                  </div>

                  {/* Skill Gaps */}
                  <div className="bg-orange-50 rounded-lg p-4">
                    <AlertTriangle className="h-6 w-6 mx-auto mb-2 text-orange-500" />
                    <p className="text-2xl font-bold text-orange-500 text-center">
                      {tailoredResult.skill_gaps?.length || 0}
                    </p>
                    <p className="text-sm text-[#555F67] text-center">Skill Gaps</p>
                  </div>
                </div>

                {/* Key Matches List */}
                {tailoredResult.key_matches?.length > 0 && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="font-medium text-green-800 mb-2 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      Skills That Match
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {tailoredResult.key_matches.map((skill, idx) => (
                        <span 
                          key={idx}
                          className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Skill Gaps List */}
                {tailoredResult.skill_gaps?.length > 0 && (
                  <div className="bg-orange-50 rounded-lg p-4">
                    <h4 className="font-medium text-orange-800 mb-2 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      Skills to Develop
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {tailoredResult.skill_gaps.map((skill, idx) => (
                        <span 
                          key={idx}
                          className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Changes Summary */}
                {tailoredResult.changes_summary && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-medium text-blue-800 mb-2">Changes Made</h4>
                    <p className="text-sm text-blue-700">{tailoredResult.changes_summary}</p>
                  </div>
                )}

                {/* Tailored Resume Content */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-[#555F67]">Tailored Resume</Label>
                    {!isEditing ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsEditing(true)}
                        className="text-[#4A728D]"
                        data-testid="edit-resume-btn"
                      >
                        <Edit3 className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setIsEditing(false);
                            setEditedContent(tailoredResult.tailored_content);
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSaveEdit}
                          disabled={savingEdit}
                          className="bg-[#A3C3D8] hover:bg-[#8FB3C9] text-[#152A38]"
                          data-testid="save-edit-btn"
                        >
                          {savingEdit ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Save className="h-4 w-4 mr-1" />
                              Save
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {isEditing ? (
                    <Textarea
                      value={editedContent}
                      onChange={(e) => setEditedContent(e.target.value)}
                      className="min-h-[400px] font-mono text-sm"
                      data-testid="resume-editor"
                    />
                  ) : (
                    <div 
                      className="bg-white border rounded-lg p-4 max-h-[400px] overflow-y-auto whitespace-pre-wrap font-mono text-sm text-[#555F67]"
                      data-testid="tailored-resume-preview"
                    >
                      {tailoredResult.tailored_content}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-between pt-4 border-t">
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setTailoredResult(null)}
                      className="border-slate-200"
                    >
                      Generate Another
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleDownloadPdf}
                      className="border-[#A3C3D8] text-[#4A728D] hover:bg-[#A3C3D8]/10"
                      data-testid="download-resume-btn"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download PDF
                    </Button>
                  </div>
                  <Button
                    onClick={onClose}
                    className="bg-[#A3C3D8] hover:bg-[#8FB3C9] text-[#152A38]"
                    data-testid="accept-resume-btn"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Accept & Close
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Modal */}
      {showUploadModal && (
        <ResumeUploadModal
          onClose={() => setShowUploadModal(false)}
          onUploadSuccess={handleResumeUploaded}
        />
      )}
    </>
  );
};

export default ResumeTailorModal;
